import { HL7AckProcessing, AdditionalVisitDto } from '@lmt/shared-types';
import * as yup from 'yup';
import { HL7SettingsData } from './types';
import i18next from 'i18next';

function t(value: string, options?: any) {
  return i18next.t(`translation:errors.${value}` as any, options);
}

export const hl7SettingsDataYupSchema: yup.Schema<Partial<HL7SettingsData>> =
  yup.object({
    // General
    onlyProcessMessagesForExistingPatients: yup.boolean(),
    subtenantId: yup.number(),
    ackProcessing: yup.mixed<HL7AckProcessing>().required(),
    facility: yup.string(),
    appName: yup.string(),
    encoding: yup.string(),
    deleteAfterMonths: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(1, () => t('min', { min: 1 }))
      .max(1000, () => t('max', { max: 1000 }))
      .required(() => t('required')),
    // Incoming settings
    incomingTransporterEnabled: yup.boolean(),
    defaultVisitTypeId: yup.number(),
    visitStartTimeInSecondsSinceMidnight: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('overwriteTime', {
        is: true,
        then: (schema) =>
          schema
            .transform((value) => (isNaN(value) ? undefined : value))
            .required(() => t('required')),
      }),
    visitLengthInHours: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    visitSearchBackwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    visitSearchForwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    caseSearchBackwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    caseSearchForwardDaysCount: yup
      .number()
      .min(0, () => t('positive'))
      .max(100, () => t('max_number', { maxNumber: 100 }))
      .required(() => t('required')),
    additionalVisits: yup.array().of(
      yup
        .object()
        .shape({
          type: yup.string().required(() => t('required')),
          visitStartTimeInSecondsSinceMidnight: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .min(0, () => t('min_number', { minNumber: 0 }))
            .required(() => t('required')),
          visitLengthInSeconds: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .min(0, () => t('min_number', { minNumber: 0 }))
            .required(() => t('required')),
          visitTypeId: yup.number().required(() => t('required')),
        })
        .defined(),
    ),
    overwriteTime: yup.boolean(),
    overwriteTimeOption: yup.mixed() as any,
    // Outgoing settings
    outgoingTransporterEnabled: yup.boolean(),
    isHl7OutgoingEnabled: yup.boolean(),
    serverAddress: yup.string().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: (schema) => schema.required(),
    }),
    /** Delays to retry failing sending attempts (in seconds)  */
    retryDelays: yup.string().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: (schema) => schema.required(),
    }),
    serverPort: yup.number().when('isHl7OutgoingEnabled', {
      is: (value: any) => !!value,
      then: (schema) =>
        schema
          .typeError(() =>
            i18next.t('translation:tenant_settings.hl7.port_error_message'),
          )
          .required(),
    }),
    filesDirectory: yup.string().when(['isHl7OutgoingEnabled'], {
      is: (isHl7OutgoingEnabled: boolean) => isHl7OutgoingEnabled,
      then: (schema) => schema.required(),
    }),
  });
